import React, {useContext, useEffect} from "react"
import Row from "@amzn/meridian/row";
import {ASLInductAreaContext} from "./context/ASLInductAreaContext";
import {FormattedMessage, injectIntl} from "react-intl";
import {chainWalk, getRefreshInterval} from "../util/Util";
import Table, {TableCell, TableRow} from "@amzn/meridian/table";
import Column from "@amzn/meridian/column";
import Heading from "@amzn/meridian/heading";
import {ASL_PREFIX, TOTAL_STRING, FEATURES, ACTION_TYPE} from "../util/Constants";
import Tooltip from "@amzn/meridian/tooltip";
import {getFeaturesFromParent} from "../handler/ParentDataHandler";
import {ConfigContext} from "../global/context/ConfigContext";

const ASLInductArea = ({intl}) => {
    const {viewModel, dispatch} = useContext(ASLInductAreaContext)
    const {configViewModel, configDispatch} = useContext(ConfigContext)

    const getDataForInductArea = () => {
        dispatch({type: 'LOAD_VIEW', data:{filter: [], dispatcher: dispatch}});
    }

    useEffect(() => {
        getDataForInductArea()
        const interval = setInterval(() => {
            if (!document.hidden) {
                getDataForInductArea();
            }
        }, getRefreshInterval());
        return () => clearInterval(interval);
    },[])

    useEffect(() => {
        getFeaturesFromParent([FEATURES.ASL_LEGACY_PROTOCOL]).then(featureStatus => {
            configDispatch({type: ACTION_TYPE.GET_FEATURES_STATUS, featureStatus: featureStatus.data});
        })
    }, [])

    const isASLLegacyProtocol = chainWalk(() => configViewModel.featureStatus[FEATURES.ASL_LEGACY_PROTOCOL], false);
    const inductLocations = Object.keys(viewModel.inductAreaMap);

    const renderTableRow = (locationName) => {
        const inductAreaMap = viewModel.inductAreaMap[locationName]
        const mheInductAreaMap = viewModel.mheInductAreaMap[locationName]
        return (
            <TableRow>
                <TableCell>{locationName}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => mheInductAreaMap.maxPPH, 0)}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => inductAreaMap.inductRate, 0)}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => inductAreaMap.volumeInducted, 0)}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => (inductAreaMap.volumeInducted/viewModel.inductAreaMap[TOTAL_STRING].volumeInducted*100).toFixed(2), 0)}</TableCell>
                <TableCell alignmentHorizontal="right">{(!isASLLegacyProtocol) ? chainWalk(() => (mheInductAreaMap.success/mheInductAreaMap.total*100).toFixed(2), 0): "NA"}</TableCell>
                <TableCell alignmentHorizontal="right">{(!isASLLegacyProtocol) ? chainWalk(() => mheInductAreaMap.kickout, 0): "NA"}</TableCell>
                <TableCell alignmentHorizontal="right">{(!isASLLegacyProtocol) ? chainWalk(() => mheInductAreaMap.noRead, 0): "NA"}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => inductAreaMap.inductTimeoutCount, 0)}</TableCell>
                <TableCell alignmentHorizontal="right">{(!isASLLegacyProtocol) ? chainWalk(() => inductAreaMap.inductFailedVerificationCount + mheInductAreaMap.verificationFailure, 0): "NA"}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => inductAreaMap.sidelineCount, 0)}</TableCell>
            </TableRow>
        )
    }

    const renderTableHeaderCell = (id, displayMessage, desc) => {
        const tooltipId = "tooltip." + id;
        const messageId = "induct." + id;
        return (<Tooltip position="right" title={intl.formatMessage({
            id: tooltipId,
            defaultMessage: desc
        })}>
            <FormattedMessage id={messageId} defaultMessage={displayMessage}/>
        </Tooltip>)
    }

    return (
            <Column spacingInset="none none none large">
                <Heading level={2} type="h400">
                    <FormattedMessage id="induct.aslInductArea" defaultMessage="ASL Induct Area"/>
                </Heading>
                <Row widths={["grid-11"]} alignmentVertical="top" className="enable-scroll-x" spacingInset="none none none large">
                <Table headerRows={1} spacing="small" alignmentHorizontal="left">
                        <TableRow>
                        <TableCell/>
                        <TableCell sortColumn="maxInductRate" alignmentHorizontal="right">
                            {renderTableHeaderCell("max_induct_rate", "Max Induct rate",
                                "Maximum expected induct rate for the ASL machine.")}
                        </TableCell>
                        <TableCell sortColumn="inductRate" alignmentHorizontal="right">
                            {renderTableHeaderCell("induct_rate", "Induct rate",
                                "Average number of packages inducted per hour by the ASL machine.")}
                        </TableCell>
                        <TableCell sortColumn="totalInducted" alignmentHorizontal="right">
                            {renderTableHeaderCell("total_inducted", "Total Inducted",
                                "Total number of packages inducted by a particular ASL machine in the current shift.")}
                        </TableCell>
                        <TableCell sortColumn="stationsPercentage" alignmentHorizontal="right">
                            {renderTableHeaderCell("station_percentage", "% of station",
                                "Percentage of total station volume that was inducted using the ASL machine.")}
                        </TableCell>
                        <TableCell sortColumn="successfulPercentage" alignmentHorizontal="right">
                            {renderTableHeaderCell("successful_percentage", "% Successful",
                                "Percentage of packages that were successfully scanned by the ASL machine.")}
                        </TableCell>
                        <TableCell sortColumn="kickout" alignmentHorizontal="right">
                            {renderTableHeaderCell("kickout", "Kickout",
                                "Number of packages that were kicked-out during ASL induct.")}
                        </TableCell>
                        <TableCell sortColumn="noRead" alignmentHorizontal="right">
                            {renderTableHeaderCell("noread", "NoRead",
                                "Number of ASL processed packages where a valid barcode was not read by the entry camera.")}
                        </TableCell>
                        <TableCell sortColumn="timeoutRate" alignmentHorizontal="right">
                            {renderTableHeaderCell("timeout", "Timeout",
                                "Number of ASL processed package scans which did not receive induct response within the SLA.")}
                        </TableCell>
                        <TableCell sortColumn="failedVerification" alignmentHorizontal="right">
                            {renderTableHeaderCell("failed_verification", "Failed Verification",
                                "Number of ASL processed packages which were not verified by the exit camera.")}
                        </TableCell>
                        <TableCell sortColumn="sideline" alignmentHorizontal="right">
                            {renderTableHeaderCell("sidelined", "Sidelined",
                                "Number of packages deemed sidelined during induct.")}
                        </TableCell>
                        </TableRow>
                    {inductLocations.filter(inductLocation => inductLocation.startsWith(ASL_PREFIX)).map(locationName => renderTableRow(locationName))}
                    </Table>
                </Row>
            </Column>
    )
}

export default injectIntl(ASLInductArea)



// WEBPACK FOOTER //
// ./src/components/technician/ASLInductArea.js